import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { Input, InputRef } from "antd";
import styled from "styled-components";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";

const CodeContainer = styled.div`
	width: 100%;
	display: flex;
	gap: 16px;
	justify-content: space-between;
	margin-bottom: 0;
`;

// 1. Change $isComplete to $isSuccess
const StyledCodeInput = styled(Input)<{ $isSuccess: boolean; $index: number }>`
	width: 48px;
	min-width: 0;
	flex: 1 1 48px;
	max-width: 48px;
	height: 56px;
	padding-inline: 0;
	text-align: center;
	font-size: 32px !important;
	font-weight: 400;
	border-radius: 8px;
	border: 1px solid #d9d9d9;
	transition: border-color 0.2s ease;

	&:hover,
	&:focus {
		border-color: ${CUSTOM_COLORS.PRIMARY};
	}

	&:focus {
		box-shadow: 0 0 0 2px rgba(58, 12, 163, 0.1);
	}

	&:disabled {
		color: #000000 !important;
		background-color: #ffffff; /* Optional: keep background clean */
		cursor: not-allowed;
	}

	/* 2. Animate ONLY when $isSuccess is true */
	${({ $isSuccess, $index }) =>
		$isSuccess &&
		`
        animation: lightUpBlue 0.3s ease forwards;
        animation-delay: ${$index * 0.08}s; 
        
        &:focus {
            box-shadow: 0 0 0 3px rgba(68, 96, 239, 0.2);
        }
    `}

	@keyframes lightUpBlue {
		0% {
			border-color: #d9d9d9;
			background-color: transparent;
		}
		40% {
			border-color: #4460ef;
			background-color: rgba(68, 96, 239, 0.06);
		}
		100% {
			border-color: #2dccd3;
			background-color: transparent;
		}
	}
`;

export interface CodeInputProps {
	length?: number;
	onComplete?: (code: string) => void;
	onChange?: (code: string) => void;
	disabled?: boolean;
	autoFocus?: boolean;
	resetSignal?: number;
	isSuccess?: boolean; // 3. Add isSuccess prop
}

export interface CodeInputRef {
	clearCode: () => void;
}

const CodeInput = forwardRef<CodeInputRef, CodeInputProps>(
	({ length = 6, onComplete, onChange, disabled = false, autoFocus = true, resetSignal, isSuccess = false }, ref) => {
		const [code, setCode] = useState<string[]>(new Array(length).fill(""));
		const inputRefs = useRef<(InputRef | null)[]>([]);

		const isAllowedControlKey = (key: string) =>
			["Backspace", "Delete", "Tab", "Enter", "Escape", "ArrowLeft", "ArrowRight", "Home", "End"].includes(key);

		useEffect(() => {
			if (autoFocus && inputRefs.current[0]) {
				inputRefs.current[0]?.focus();
			}
		}, [autoFocus]);

		useEffect(() => {
			setCode(new Array(length).fill(""));
			onChange?.("");
			const focusFrame = window.requestAnimationFrame(() => inputRefs.current[0]?.focus());
			return () => window.cancelAnimationFrame(focusFrame);
		}, [resetSignal, length]);

		const handleCodeChange = (index: number, value: string) => {
			const normalizedValue = value.replace(/\D/g, "").slice(0, 1);

			const newCode = [...code];
			newCode[index] = normalizedValue;
			setCode(newCode);

			const currentCode = newCode.join("");
			onChange?.(currentCode);

			if (currentCode.length === length && !currentCode.includes("")) {
				onComplete?.(currentCode);
			}

			if (normalizedValue && index < length - 1) {
				inputRefs.current[index + 1]?.focus();
			}
		};

		const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
			if (e.ctrlKey || e.metaKey || e.altKey) return;

			if (!isAllowedControlKey(e.key) && !/^\d$/.test(e.key)) {
				e.preventDefault();
				return;
			}

			if (e.key === "Backspace" && !code[index] && index > 0) {
				inputRefs.current[index - 1]?.focus();
			}
		};

		const handlePaste = (e: React.ClipboardEvent) => {
			e.preventDefault();
			const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
			const newCode = [...code];

			for (let i = 0; i < pastedData.length; i++) {
				newCode[i] = pastedData[i];
			}

			setCode(newCode);

			const currentCode = newCode.join("");
			onChange?.(currentCode);

			if (currentCode.length === length && !currentCode.includes("")) {
				onComplete?.(currentCode);
			}

			const nextIndex = Math.min(pastedData.length, length - 1);
			inputRefs.current[nextIndex]?.focus();
		};

		const clearCode = () => {
			const emptyCode = new Array(length).fill("");
			setCode(emptyCode);
			onChange?.("");
			if (inputRefs.current[0]) inputRefs.current[0]?.focus();
		};

		useImperativeHandle(ref, () => ({ clearCode }));

		return (
			<CodeContainer>
				{code.map((digit, index) => (
					<StyledCodeInput
						key={index}
						$index={index}
						data-filled={Boolean(digit)}
						ref={(el) => (inputRefs.current[index] = el)}
						value={digit}
						onChange={(e) => handleCodeChange(index, e.target.value)}
						onKeyDown={(e) => handleKeyDown(index, e)}
						onPaste={handlePaste}
						maxLength={1}
						inputMode="numeric"
						pattern="[0-9]*"
						autoComplete="off"
						disabled={disabled || isSuccess} // Lock inputs during success animation
						$isSuccess={isSuccess} // 4. Pass down the success state
					/>
				))}
			</CodeContainer>
		);
	},
);

CodeInput.displayName = "CodeInput";

export default CodeInput;
